import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchUserStatus, clearStatusCache } from "@/lib/subscription";

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Clear cache first to ensure fresh fetch
    clearStatusCache();
    
    // Fetch fresh status from server with retry
    const fetchStatus = async () => {
      try {
        // Force refresh to get latest from server
        const status = await fetchUserStatus(true);
        setUserStatus(status);
        setLoading(false);
      } catch {
        // Retry once after short delay
        setTimeout(async () => {
          try {
            const status = await fetchUserStatus(true);
            setUserStatus(status);
          } catch {
            // Use fallback
          }
          setLoading(false);
        }, 1000);
      }
    };
    
    fetchStatus();
  }, []);

  const tier = userStatus?.plan || userStatus?.tier || 'decision_partner';
  const remaining = userStatus?.remaining ?? 2;
  const tierName = tier === 'enterprise' ? 'Enterprise' : 'Decision Partner';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full text-center"
      >
        <div className="space-y-6">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
            <Check className="h-8 w-8 text-primary" />
          </div>
          
          <div className="space-y-2">
            <h1 className="font-heading text-2xl text-foreground">
              {tierName} unlocked
            </h1>
            <p className="text-muted-foreground">
              Decisora will now remember how you think and help you decide with confidence.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              You have {remaining} decisions available this month.
            </p>
          </div>

          <Button
            onClick={() => navigate("/")}
            className="h-11 px-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Continue deciding
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
